import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
	Building2,
	CheckCircle2,
	Globe,
	Instagram,
	Linkedin,
	Mail,
	MapPin,
	Phone,
	Twitter,
} from "lucide-react";
import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { slugifyURL } from "@/app/handlers/urlFunctions";
import { useCompany, useCompanyJobs } from "@/app/services/create-resume/queries";
import JobListingItem from "@/app/Views/JobsPage/components/JobListingItem";

import { VerifiedBadge } from "./CompanyDashboard/shared";

dayjs.extend(relativeTime);

const DEFAULT_BENEFITS = [
	"Meaningful work with visible impact",
	"Growth opportunities across teams",
	"Collaborative, candidate-first culture",
];

function ensureUrl(value) {
	if (!value || typeof value !== "string") return "";
	return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function normalizeEmploymentType(value) {
	switch (value) {
		case "Full-Time":
			return "Full-time";
		case "Part-Time":
			return "Part-time";
		case "Contractor":
			return "Contract";
		case "Temporary":
			return "Temporary";
		case "Internship":
			return "Internship";
		default:
			return value || "Other";
	}
}

function formatLocation(location, streetAddress) {
	const parts = [streetAddress, location?.city, location?.region, location?.country].filter(Boolean);
	return parts.join(", ") || "Location not specified";
}

function toDateString(value) {
	if (!value) return "";
	if (typeof value === "string") {
		const parsed = dayjs(value);
		return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
	}

	if (!value.year || !value.month || !value.day) return "";
	return dayjs(new Date(value.year, value.month - 1, value.day)).format("YYYY-MM-DD");
}

function toRelativeDate(value) {
	if (!value) return "Recently";
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.fromNow() : "Recently";
}

function isClosedJob(jobStatus, deadline) {
	if (jobStatus && ["Closed", "Stopped", "Rejected"].includes(jobStatus)) {
		return true;
	}

	return Boolean(deadline) && dayjs(deadline).isBefore(dayjs(), "day");
}

function buildSocialLinks(company) {
	const links = [];

	if (company?.linkedin_url) {
		links.push({ platform: "LinkedIn", url: company.linkedin_url });
	}

	if (company?.twitter_url) {
		links.push({ platform: "Twitter", url: company.twitter_url });
	}

	if (company?.instagram_url) {
		links.push({ platform: "Instagram", url: company.instagram_url });
	}

	if (company?.company_website) {
		links.push({ platform: "Website", url: ensureUrl(company.company_website) });
	}

	return links.filter((link) => link.url);
}

function getSocialIcon(platform) {
	switch (platform) {
		case "LinkedIn":
			return <Linkedin className="h-4 w-4" />;
		case "Twitter":
			return <Twitter className="h-4 w-4" />;
		case "Instagram":
			return <Instagram className="h-4 w-4" />;
		default:
			return <Globe className="h-4 w-4" />;
	}
}

function isOpenJob(job) {
	const deadline = toDateString(job?.deadlineDate) || toDateString(job?.closeAt);
	return !isClosedJob(job?.jobStatus, deadline);
}

function LoadingState() {
	return (
		<div className="min-h-screen bg-gray-100">
			<div className="h-60 w-full animate-pulse bg-gray-200" />
			<div className="mx-auto max-w-6xl px-6 pb-12">
				<div className="-mt-12 relative z-10 rounded-xl border border-border bg-white p-6 shadow-sm">
					<div className="flex items-end gap-4 pb-6">
						<div className="h-32 w-32 animate-pulse rounded-full bg-gray-200 ring-4 ring-white" />
						<div className="pb-2">
							<div className="mb-2 h-6 w-56 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
						</div>
					</div>
					<div className="space-y-4">
						<div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-full animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-full animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-4/5 animate-pulse rounded bg-gray-200" />
					</div>
				</div>
			</div>
		</div>
	);
}

function ErrorState() {
	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-100 px-6">
			<div className="w-full max-w-lg rounded-xl border border-border bg-white p-8 text-center shadow-sm">
				<h1 className="text-xl font-semibold text-foreground">Company not found</h1>
				<p className="mt-2 text-sm text-muted-foreground">
					We could not load this verified company page.
				</p>
			</div>
		</div>
	);
}

export default function CompanyVerifiedProfile() {
	const navigate = useNavigate();
	const { companyId } = useParams();
	const { data: companyResponse, isLoading: isCompanyLoading, isError: isCompanyError } = useCompany(companyId);
	const { data: jobsResponse, isLoading: isJobsLoading } = useCompanyJobs(companyId);

	const company = companyResponse?.data;
	const socialLinks = React.useMemo(() => buildSocialLinks(company), [company]);
	const whyWorkWithUs = React.useMemo(() => {
		return Array.isArray(company?.benefits) && company.benefits.length ? company.benefits : DEFAULT_BENEFITS;
	}, [company?.benefits]);
	const openJobs = React.useMemo(() => {
		const jobs = Array.isArray(jobsResponse?.data) ? jobsResponse.data : [];
		return jobs
			.filter(isOpenJob)
			.map((job) => ({
				...job,
				logo: company?.logo_url || "/images/co-placeholder.png",
				company: typeof job?.company === "object"
					? {
						...job.company,
						_id: job.company?._id || company?._id || companyId,
						company_name: job.company?.company_name || company?.company_name || "Company",
					}
					: {
						_id: company?._id || companyId,
						company_name: company?.company_name || job?.companyName || "Company",
					},
			}));
	}, [company?._id, company?.company_name, company?.logo_url, companyId, jobsResponse?.data]);
	const companyName = company?.company_name || "Company";
	const industry = company?.company_industry || "Company";
	const about = company?.about_us || "No company description available.";
	const website = company?.company_website ? ensureUrl(company.company_website) : "";
	const email = company?.contact_email || "";
	const phone = company?.phone_number || "";
	const address = company?.headquarters || "";
	const logoUrl = company?.logo_url || "";
	const primaryColor = "#0A6FE2";
	const pageTitle = `${companyName} | Dproz`;
	const handleExploreJob = React.useCallback(
		(job) => {
			const slug = slugifyURL({
				_id: job?._id,
				companyName: job?.company?.company_name || job?.companyName || companyName,
				jobTitle: job?.jobTitle || "job",
			});
			navigate(`/jobs/${slug}`);
		},
		[companyName, navigate],
	);

	if (isCompanyLoading) {
		return <LoadingState />;
	}

	if (isCompanyError || !company) {
		return <ErrorState />;
	}

	return (
		<div className="min-h-screen bg-gray-100">
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={about} />
				<meta property="keywords" content={companyName} />
				<meta property="og:image" content={logoUrl} />
				<meta property="og:image:secure_url" content={logoUrl} />
				<meta property="og:image:alt" content={`${companyName} logo`} />
				<meta property="og:image:type" content="image/png" />
				<meta property="og:title" content={companyName} />
				<meta property="og:description" content={about} />
				<meta property="og:type" content="website" />
			</Helmet>

			<div
				className="h-60 w-full"
				style={{
					background: `linear-gradient(to right, ${primaryColor}99, ${primaryColor})`,
				}}
			/>

			<div className="mx-auto max-w-6xl px-6 pb-12">
				<div className="-mt-12 relative z-10 rounded-xl border border-border bg-white shadow-sm">
					<div className="relative z-10 -mt-12 px-6 pb-6 pt-0">
						<div className="flex items-end gap-4">
							<div
								className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full ring-4 ring-white shadow-lg"
								style={{
									background: `linear-gradient(to bottom right, ${primaryColor}CC, ${primaryColor})`,
								}}
							>
								{logoUrl ? (
									<img src={logoUrl} alt={companyName} className="h-full w-full object-cover" />
								) : (
									<Building2 className="h-10 w-10 text-white" />
								)}
							</div>

							<div className="pb-2">
								<div className="flex items-center gap-2">
									<h1 className="text-xl font-semibold text-foreground">{companyName}</h1>
									<VerifiedBadge size="sm" />
								</div>
								<p className="text-sm text-muted-foreground">{industry}</p>
							</div>
						</div>
					</div>

					<div className="space-y-6 p-6 pt-0">
						<div>
							<h3 className="mb-2 text-sm font-semibold text-foreground">About</h3>
							<p className="whitespace-pre-wrap text-sm text-muted-foreground">{about}</p>
						</div>

						<div>
							<h3 className="mb-3 text-sm font-semibold text-foreground">Contact Information</h3>
							<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
								{website ? (
									<div className="flex items-center gap-2 text-sm">
										<Globe className="h-4 w-4 text-muted-foreground" />
										<a href={website} className="text-brand-600 hover:underline" target="_blank" rel="noopener noreferrer">
											{website}
										</a>
									</div>
								) : null}
								{email ? (
									<div className="flex items-center gap-2 text-sm">
										<Mail className="h-4 w-4 text-muted-foreground" />
										<a href={`mailto:${email}`} className="text-brand-600 hover:underline">
											{email}
										</a>
									</div>
								) : null}
								{phone ? (
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Phone className="h-4 w-4" />
										<span>{phone}</span>
									</div>
								) : null}
								{address ? (
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<MapPin className="h-4 w-4" />
										<span>{address}</span>
									</div>
								) : null}
							</div>
						</div>

						<div>
							<h3 className="mb-3 text-sm font-semibold text-foreground">Social Links</h3>
							<div className="flex flex-wrap gap-2">
								{socialLinks.map((link) => (
									<a
										key={`${link.platform}-${link.url}`}
										href={link.url}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-secondary/80"
									>
										<span className="text-muted-foreground">{getSocialIcon(link.platform)}</span>
										<span className="text-sm font-medium">{link.platform}</span>
									</a>
								))}
							</div>
						</div>

						<div className="rounded-lg border border-secondary bg-secondary/30 p-4">
							<h3 className="mb-3 text-sm font-semibold text-foreground">Why Work With Us</h3>
							<ul className="space-y-2">
								{whyWorkWithUs.map((point, index) => (
									<li key={`${point}-${index}`} className="flex items-center gap-2">
										<CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
										<span className="text-sm text-muted-foreground">{point}</span>
									</li>
								))}
							</ul>
						</div>

						<div>
							<h3 className="mb-3 text-sm font-semibold text-foreground">
								Open Positions
								<span className="ml-2 font-normal text-muted-foreground">({openJobs.length})</span>
							</h3>
							<div className="space-y-3">
								{openJobs.map((job) => (
									<JobListingItem
										key={job._id}
										job={job}
										onApply={() => handleExploreJob(job)}
										showCompany={false}
										titleClassName="text-sm"
										hideDate={!job?.deadlineDate?.year || !job?.deadlineDate?.month || !job?.deadlineDate?.day}
									/>
								))}
								{!isJobsLoading && openJobs.length === 0 ? (
									<div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
										No open positions available right now.
									</div>
								) : null}
							</div>
						</div>
					</div>
				</div>

				<p className="mt-4 text-center text-xs text-muted-foreground">
					This is a verified employer page on <span className="font-medium text-foreground">Dproz</span>
				</p>
			</div>
		</div>
	);
}
